<template>
  <section>
    <div class="topup-error" v-if="isFormErrorToDisplay()">
      <h3 v-html="$t('component.stage1.formErrorHeader')"></h3>
      <div v-if="isNoTopupSet">
        <p v-html="$t('component.stage1.formErrorPhone')"></p>
      </div>
      <div v-if="!isEmailValid()">
        <p v-html="$t('component.stage1.formErrorEmail')"></p>
      </div>
      <div v-if="isAnyTopupCheckphoneError">
        <p v-for="(topup, index) in topupsWithCheckphoneError" :key="index">
          {{ $t('component.stage1.formErrorCheckphone', {phone: topup.phone}) }}
        </p>
      </div>
    </div>
    <form class="topup-form" @submit="submit">
      <div class="topup-form-frame">
        <div class="topup-loading" v-if="isLoading">{{ $t('component.stage1.loadingText') }}</div>
        <div v-if="!isLoading">
          <h1 class="step-title" v-html="$t('component.stage1.title')"></h1>
          <h2 class="dark-title" v-if="!canShowContacts" v-html="$t('component.stage1.header')"></h2>
          <h2 class="dark-title" v-if="canShowContacts" v-html="$t('component.stage1.headerLogged1')"></h2>
          <div v-if="canShowContacts" class="topup-contacts">
            <ul class="topup-contacts-list">
              <li v-for="(contact, index) in contacts"
                  @click="toggleContact(contact)"
                  @keydown.space.prevent="toggleContact(contact)"
                  :key="index"
                  tabindex="0"
                  :title="isContactActive(contact.msisdn) ? $t('component.stage1.a11y.unselectNumberButton') : $t('component.stage1.a11y.chooseNumberButton')"
                  class="topup-contacts-item"
                  :class="{
                  checking: isContactChecking(contact.msisdn),
                  active: isContactActive(contact.msisdn),
                  error: isContactError(contact.msisdn)
                }">
                <div>
                  <i class="item-icon el-icon-user-solid"></i>
                  <div class="item-name">{{ contact.description | truncate(20) }}</div>
                  <div class="item-number">({{ contact.msisdn | phone }})</div>
                  <i class="info el-icon-check"
                     v-if="isContactActive(contact.msisdn) && !isContactChecking(contact.msisdn)"></i>
                  <el-tooltip :content="$t('component.stage1.contactCheckphoneError')" placement="top"
                              v-if="isContactError(contact.msisdn)">
                    <i class="info el-icon-warning"></i>
                  </el-tooltip>
                </div>
              </li>
            </ul>
          </div>
          <h2 class="dark-title" v-if="canShowContacts && isAnyNonContactTopup()" v-html="$t('component.stage1.headerLogged2')"></h2>
          <div class="topup-form-fields">
            <div>
              <div v-for="(topup, topupIndex) in form.topup" :key="topupIndex">
                <section class="topup-form-field-zone" v-if="!topup.isContact">
                  <div class="topup-form-field">
                    <div class="topup-form-field-container" :class="{error: hasTopupError(topupIndex)}">
                      <label :for="'form-phone-' + topupIndex">{{ $t('component.stage1.labelPhone') }} *</label>
                      <input type="text"
                             :id="'form-phone-' + topupIndex"
                             class="topup-phone"
                             ref="topupPhoneInput"
                             v-model="topup.phone"
                             data-matomo-unmask
                             :disabled="isCheckPhoneInProgress(topupIndex)"
                             autocomplete="on"
                             @keyup="checkPhone(topupIndex, $event)"
                             @paste="checkPhoneOnPaste($event, topupIndex)"
                             v-mask="'###-###-###'"
                             placeholder="___-___-___">
                      <button type="button"
                              class="remove-topup"
                              @click="removeTopup(topupIndex)"
                              :title="$t('component.stage1.a11y.removeNumberButton')"
                              v-if="canRemoveTopup() && !isCheckPhoneInProgress(topupIndex)">
                        <font-awesome-icon :icon="['far', 'trash-alt']"/>
                      </button>
                      <font-awesome-icon :icon="['fas', 'spinner']" spin v-if="isCheckPhoneInProgress(topupIndex)"/>
                    </div>
                    <div class="topup-form-error-container phone-error" v-if="hasTopupError(topupIndex)">
                      <ul>
                        <li v-if="isPhoneEmpty(topupIndex)">{{ $t('component.stage1.errorPhoneRequired') }}</li>
                        <li v-if="!isPhoneEmpty(topupIndex) && !isPhoneValid(topupIndex)">{{ $t('component.stage1.errorPhoneInvalid') }}</li>
                        <li v-if="isCheckPhoneError(topupIndex)">{{ $t('component.stage1.errorPhoneCheck') }}</li>
                      </ul>
                    </div>
                  </div>
                </section>
              </div>
            </div>
            <div v-if="!isUserLogged">
              <section class="topup-form-field-zone">
                <div class="topup-form-field">
                  <div class="topup-form-field-container" :class="{error: hasEmailError()}">
                    <label for="form-email">{{ $t('component.stage1.labelEmail') }} *</label>
                    <input type="text"
                           id="form-email"
                           v-model="form.email"
                           data-matomo-unmask
                           @blur="checkEmail"
                           @focus="setEmailValid(null)"
                           :placeholder="$t('component.stage1.placeholderEmail')">
                  </div>
                  <div class="topup-form-error-container email-error" v-if="hasEmailError()">
                    <ul>
                      <li v-if="isEmailEmpty()">{{ $t('component.stage1.errorEmailRequired') }}</li>
                      <li v-if="!isEmailEmpty() && !isEmailValid()">{{ $t('component.stage1.errorEmailInvalid') }}</li>
                    </ul>
                  </div>
                  <div class="topup-form-description">
                    {{ $t('component.stage1.emailDescription') }}
                  </div>
                </div>
              </section>
            </div>
          </div>
          <div class="topup-form-additional-button" v-if="canAddTopup()">
            <button type="button"
                    id="add-topup"
                    :title="$t('component.stage1.a11y.addNumberButton')"
                    @click="addTopup">
            <span>
              <i class="el-icon-circle-plus"></i>
              <strong>{{ $t('component.stage1.addPhone') }}</strong> {{
                $t('component.stage1.addPhoneMaxInfo', { count: MAX_TOPUPS })
              }}
            </span>
            </button>
          </div>
        </div>
      </div>
      <div class="topup-form-buttons">
        <span>* {{ $t('component.stage1.required') }}</span>
        <button type="submit"
                :disabled="isLoading || isMaintenanceGlobal"
                id="submit-step-1"
                :title="$t('component.autotopup.stage1.a11y.toNextStepButton')">
          {{ $t('component.stage1.buttonNext') }} ›
        </button>
      </div>
    </form>
  </section>
</template>

<script>
import TopupStep1Mixin from '@/common/mixins/Topup/Stage1.mixin'

export default {
  name: 'TopupStage1',
  mixins: [TopupStep1Mixin],
  mounted () {
    let topupField = this.$refs.topupPhoneInput
    if (topupField) {
      topupField[0].focus()
    }
  }
}
</script>

<style src="../../../assets/scss/topup-form.scss" lang="scss"></style>
